<script lang="ts">
  import { getContext } from 'svelte';
  import { cx } from '@emotion/css';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { key, type SanityContext } from '$lib/context/sanity';
  import type { IconStack } from '$lib/sanity/schemas';
  import { TwistStack, ResponsiveVars } from '@twist/core';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import PortableText from '$components/SanityComponents/PortableText/PortableText.svelte';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: IconStack;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const { urlBuilder } = getContext<SanityContext>(key);

  const imgSrc = urlBuilder.image(document.icon.svg.asset).auto('format').url();
  const responsiveVars = new ResponsiveVars()
    .addFluid('icon-size', toFluidVarInput(90, 90))
    .addFluid('horizontal-icon-margin-right', toFluidVarInput(19, 19))
    .addFluid('vertical-icon-margin-bottom', toFluidVarInput(19, 22))
    .toCSS();
</script>

<TwistStack
  direction={document.orientation === 'horizontal' ? 'row' : 'column'}
  align={document.alignment || 'flex-start'}
  justify="flex-start"
  wrap="nowrap"
  spacing={0}
>
  <div
    class={cx('icon', document.orientation, responsiveVars)}
    role="img"
    aria-label={`${document.icon.name} icon`}
    style={`--url: url(${imgSrc})`}
    data-sanity={encodeDataAttribute([...path, 'icon', 'name'])}
  ></div>
  <PortableText {sourceMap} path={[...path, 'body']} body={document.body} />
</TwistStack>

<style lang="scss">
  @import 'src/scss/variables';

  .icon {
    height: var(--icon-size);
    background-color: var(--theme-general-foreground-color, $general-foreground-color);
    -webkit-mask: var(--url) no-repeat 0 0 / contain;
    mask: var(--url) no-repeat 0 0 / contain;
    aspect-ratio: 1;

    &.horizontal {
      margin-right: var(--horizontal-icon-margin-right);
    }

    &.vertical {
      margin-bottom: var(--vertical-icon-margin-bottom);
    }
  }
</style>
